import React, { useCallback, useMemo, useState } from 'react';

const LocationContext = React.createContext(null);

const STORAGE_PREFIX = 'ao-location:';


function storageKey(sellerId) {
  return `${STORAGE_PREFIX}${sellerId || ''}`;
}


function readStored(sellerId) {
  if (!sellerId) {
    return 'all';
  }
  try {
    return window.localStorage.getItem(storageKey(sellerId)) || 'all';
  }
  catch {
    // Storage unavailable (private mode / SSR) — fall back to "all".
    return 'all';
  }
}


/** Holds the active inventory-location filter, scoped per seller and persisted
 * in localStorage. "all" means no `location` query param is sent (every yard).
 * The selector lives in the Dashboard / Inventory / Listings top bars and the
 * chosen id is threaded into those queries (and the parts export) so the views
 * scope together (#170). */
function LocationProvider({ sellerId, children }) {
  // Lazy init from storage; re-keyed below when the seller changes.
  const [state, setState] = useState(() => ({
    sellerId,
    locationId: readStored(sellerId),
  }));

  // Switching org (or first load before seller resolved) re-reads persistence.
  if (state.sellerId !== sellerId) {
    setState({ sellerId, locationId: readStored(sellerId) });
  }

  const setLocationId = useCallback((nextId) => {
    const value = nextId || 'all';
    setState((previous) => {
      if (previous.locationId === value) {
        return previous;
      }
      return { ...previous, locationId: value };
    });
    try {
      window.localStorage.setItem(storageKey(sellerId), value);
    }
    catch {
      // Persisting is best-effort.
    }
  }, [sellerId]);

  const locationId = state.sellerId === sellerId ? state.locationId : 'all';

  const contextValue = useMemo(() => ({
    locationId,
    // Convenience for query builders: undefined when "all" (omit the param).
    locationParam: locationId && locationId !== 'all' ? locationId : undefined,
    setLocationId,
  }), [locationId, setLocationId]);

  return <LocationContext.Provider value={contextValue}>{children}</LocationContext.Provider>;
}

LocationProvider.displayName = 'LocationProvider';
export { LocationContext, LocationProvider };
